import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { DiagnosticRTCServiceType } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'

export type DiagnosticRTCServiceSwitchProps = {
  onChange: (service: DiagnosticRTCServiceType) => void
  selectedService?: DiagnosticRTCServiceType
  services: DiagnosticRTCServiceType[]
}

export function DiagnosticRTCServiceSwitch(props: DiagnosticRTCServiceSwitchProps) {
  const { onChange, selectedService, services } = props
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const options = useMemo(
    () =>
      Object.keys(DiagnosticRTCServiceType)
        .filter(key => services.includes(DiagnosticRTCServiceType[key as keyof typeof DiagnosticRTCServiceType]))
        .map((key) => ({
          label: t(`page.liveConsultation.diagnosticRTCServiceSwitch.${key.toLowerCase()}`),
          value: DiagnosticRTCServiceType[key as keyof typeof DiagnosticRTCServiceType],
        })),
    [services, t]
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChange = (service: DiagnosticRTCServiceType) => () => {
    onChange(service)
  }

  return (
    <Box>
      <Button
        variant="contained"
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ borderRadius: 0, clipPath: 'polygon(38.5px 0%, 100% 0, 100% 100%, 0% 100%)', minWidth: '200px', justifyContent: 'end', pl: '40px' }}
      >
        {t('page.liveConsultation.diagnosticRTCServiceSwitch.label', { name: options.find((option) => option.value === selectedService)?.label })}
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
        sx={{ width: '100%' }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} selected={option.value === selectedService} onClick={handleChange(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default DiagnosticRTCServiceSwitch
